import { Pitch, PrerenderedPhraseData } from 'Types';
import { midiPitchToFrequency } from 'Utils';
import {
  getTargetModeForConversion,
  getMusicXmlKeyContextsByMeasure,
  getParallelModeConversionPitchDelta,
  type KeyContext,
  type KeyMode,
  type ParallelModeConversion,
} from './MusicXmlModeConversion';

const MIN_MIDI_NOTE = 0;
const MAX_MIDI_NOTE = 127;

type PitchClassMapping = {
  accidental: number;
  fundamentalNote: number;
};

const SHARP_PITCH_CLASS_MAP: Record<number, PitchClassMapping> = {
  0: { accidental: 2, fundamentalNote: 0 }, // C
  1: { accidental: 0, fundamentalNote: 0 }, // C#
  2: { accidental: 2, fundamentalNote: 2 }, // D
  3: { accidental: 0, fundamentalNote: 2 }, // D#
  4: { accidental: 2, fundamentalNote: 4 }, // E
  5: { accidental: 2, fundamentalNote: 5 }, // F
  6: { accidental: 0, fundamentalNote: 5 }, // F#
  7: { accidental: 2, fundamentalNote: 7 }, // G
  8: { accidental: 0, fundamentalNote: 7 }, // G#
  9: { accidental: 2, fundamentalNote: 9 }, // A
  10: { accidental: 0, fundamentalNote: 9 }, // A#
  11: { accidental: 2, fundamentalNote: 11 }, // B
};

const clampMidiNote = (midiNote: number): number =>
  Math.max(MIN_MIDI_NOTE, Math.min(MAX_MIDI_NOTE, midiNote));

const mod = (value: number, divisor: number): number => ((value % divisor) + divisor) % divisor;

const MODE_RELATIVE_MAJOR_OFFSET: Record<KeyMode, number> = {
  major: 0,
  minor: 3,
  harmonic_minor: 3,
  dorian: -2,
  mixolydian: -7,
};

const MAJOR_TONIC_BY_FIFTHS = [
  'Cb',
  'Gb',
  'Db',
  'Ab',
  'Eb',
  'Bb',
  'F',
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'C#',
] as const;
const MINOR_TONIC_BY_FIFTHS = [
  'Ab',
  'Eb',
  'Bb',
  'F',
  'C',
  'G',
  'D',
  'A',
  'E',
  'B',
  'F#',
  'C#',
  'G#',
  'D#',
  'A#',
] as const;
const SHARP_TONIC_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_TONIC_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const MODE_LABEL: Record<KeyMode, string> = {
  major: 'major',
  minor: 'minor',
  harmonic_minor: 'harmonic minor',
  dorian: 'dorian',
  mixolydian: 'mixolydian',
};

export type TransposedKeySignature = {
  fifths: number;
  label: string;
  mode: KeyMode;
  tonicPitchClass: number;
};

const chooseFifths = (
  relativeMajorPitchClass: number,
  sourceFifths: number,
  semitoneSteps: number,
): number => {
  const candidates: number[] = [];
  for (let fifths = -7; fifths <= 7; fifths += 1) {
    if (mod(7 * fifths, 12) === relativeMajorPitchClass) {
      candidates.push(fifths);
    }
  }

  return candidates.sort((left, right) => {
    const complexityDelta = Math.abs(left) - Math.abs(right);
    if (complexityDelta !== 0) return complexityDelta;

    const preferredDirection =
      semitoneSteps === 0 ? Math.sign(sourceFifths) : Math.sign(semitoneSteps);
    if (preferredDirection !== 0) {
      const leftMatches = Math.sign(left) === preferredDirection ? 0 : 1;
      const rightMatches = Math.sign(right) === preferredDirection ? 0 : 1;
      if (leftMatches !== rightMatches) return leftMatches - rightMatches;
    }
    return Math.abs(left - sourceFifths) - Math.abs(right - sourceFifths);
  })[0];
};

const tonicNameForKey = (tonicPitchClass: number, fifths: number, mode: KeyMode): string => {
  if (mode === 'major') return MAJOR_TONIC_BY_FIFTHS[fifths + 7];
  if (mode === 'minor' || mode === 'harmonic_minor') {
    return MINOR_TONIC_BY_FIFTHS[fifths + 7];
  }
  return (fifths < 0 ? FLAT_TONIC_NAMES : SHARP_TONIC_NAMES)[tonicPitchClass];
};

export const transposeKeyContext = (
  sourceKey: KeyContext,
  semitoneSteps: number,
  modeConversion: ParallelModeConversion = 'off',
): TransposedKeySignature => {
  const mode = getTargetModeForConversion(sourceKey.sourceMode, modeConversion);
  const tonicPitchClass = mod(sourceKey.tonicPitchClass + semitoneSteps, 12);
  const relativeMajorPitchClass = mod(tonicPitchClass + MODE_RELATIVE_MAJOR_OFFSET[mode], 12);
  const fifths = chooseFifths(relativeMajorPitchClass, sourceKey.fifths, semitoneSteps);
  const tonicName = tonicNameForKey(tonicPitchClass, fifths, mode);

  return {
    fifths,
    label: `${tonicName} ${MODE_LABEL[mode]}`,
    mode,
    tonicPitchClass,
  };
};

export const getTransposedMusicXmlKeySignature = (
  musicXml: string,
  semitoneSteps: number,
  modeConversion: ParallelModeConversion = 'off',
): TransposedKeySignature | null => {
  const sourceKey = getMusicXmlKeyContextsByMeasure(musicXml).get(0);
  return sourceKey ? transposeKeyContext(sourceKey, semitoneSteps, modeConversion) : null;
};

const KEY_SIGNATURE_SHARP_NOTES = [5, 0, 7, 2, 9, 4, 11];
const KEY_SIGNATURE_FLAT_NOTES = [11, 4, 9, 2, 7, 0, 5];

const getKeyAlterations = (fifths: number): Map<number, number> => {
  const alterations = new Map<number, number>();
  const notes = fifths > 0 ? KEY_SIGNATURE_SHARP_NOTES : KEY_SIGNATURE_FLAT_NOTES;
  for (let index = 0; index < Math.abs(fifths); index += 1) {
    alterations.set(notes[index], fifths > 0 ? 1 : -1);
  }
  return alterations;
};

const spellPitchInKey = (
  transposedMidiNote: number,
  keySignatureFifths: number,
): PitchClassMapping & { displayAccidental: Pitch['displayAccidental']; octave: number } => {
  const pitchClass = mod(transposedMidiNote, 12);
  const keyAlterations = getKeyAlterations(keySignatureFifths);

  for (const fundamentalNote of [0, 2, 4, 5, 7, 9, 11]) {
    const alteration = keyAlterations.get(fundamentalNote) ?? 0;
    if (mod(fundamentalNote + alteration, 12) === pitchClass) {
      return {
        accidental: 2,
        displayAccidental: null,
        fundamentalNote,
        octave: (transposedMidiNote - fundamentalNote - alteration) / 12 - 4,
      };
    }
  }

  const candidates = [-1, 0, 1].flatMap((alteration) =>
    [0, 2, 4, 5, 7, 9, 11]
      .filter((fundamentalNote) => mod(fundamentalNote + alteration, 12) === pitchClass)
      .map((fundamentalNote) => ({ alteration, fundamentalNote })),
  );
  const preferredDirection = keySignatureFifths < 0 ? -1 : 1;
  const candidate = candidates.sort((left, right) => {
    const score = ({ alteration, fundamentalNote }: (typeof candidates)[number]) => {
      const keyAlteration = keyAlterations.get(fundamentalNote) ?? 0;
      if (alteration === 0 && keyAlteration !== 0) return 0;
      if (alteration === preferredDirection) return 1;
      if (alteration === 0) return 2;
      return 3;
    };
    return score(left) - score(right);
  })[0];
  if (candidate) {
    const { alteration, fundamentalNote } = candidate;
    const keyAlteration = keyAlterations.get(fundamentalNote) ?? 0;
    return {
      accidental: alteration > 0 ? 0 : alteration < 0 ? 1 : 2,
      displayAccidental: alteration > 0 ? '#' : alteration < 0 ? 'b' : keyAlteration ? 'n' : null,
      fundamentalNote,
      octave: (transposedMidiNote - fundamentalNote - alteration) / 12 - 4,
    };
  }

  const fallback = SHARP_PITCH_CLASS_MAP[pitchClass];
  return {
    ...fallback,
    displayAccidental: fallback.accidental === 0 ? '#' : null,
    octave: Math.floor(transposedMidiNote / 12) - 4,
  };
};

export const transposePitchBySemitones = (
  pitch: Pitch,
  semitoneSteps: number,
  keySignatureFifths?: number,
): Pitch => {
  if (semitoneSteps === 0 && keySignatureFifths === undefined) {
    return pitch;
  }

  const sourceMidiNote = clampMidiNote(Math.round(pitch.halfTone + 12));
  const transposedMidiNote = clampMidiNote(sourceMidiNote + semitoneSteps);
  const pitchClass = ((transposedMidiNote % 12) + 12) % 12;
  const mapping =
    keySignatureFifths === undefined
      ? {
          ...SHARP_PITCH_CLASS_MAP[pitchClass],
          displayAccidental:
            SHARP_PITCH_CLASS_MAP[pitchClass].accidental === 0 ? ('#' as const) : null,
          octave: Math.floor(transposedMidiNote / 12) - 4,
        }
      : spellPitchInKey(transposedMidiNote, keySignatureFifths);

  return {
    accidental: mapping.accidental,
    displayAccidental: mapping.displayAccidental,
    fundamentalNote: mapping.fundamentalNote,
    octave: mapping.octave,
    halfTone: transposedMidiNote - 12,
    frequency: midiPitchToFrequency(transposedMidiNote),
  };
};

export const transposeSerializedIteratorBySemitones = (
  serializedIterator: PrerenderedPhraseData[],
  semitoneSteps: number,
): PrerenderedPhraseData[] => {
  if (semitoneSteps === 0) {
    return serializedIterator;
  }

  return serializedIterator.map((phraseSlice) => ({
    ...phraseSlice,
    currentVoiceEntries: phraseSlice.currentVoiceEntries.map((voiceEntry) => ({
      ...voiceEntry,
      Notes: voiceEntry.Notes.map((note) => {
        if (note.isRest) {
          return note;
        }

        return {
          ...note,
          pitch: transposePitchBySemitones(note.pitch, semitoneSteps),
        };
      }),
    })),
  }));
};

export const transformSerializedIteratorPitches = (
  serializedIterator: PrerenderedPhraseData[],
  semitoneSteps: number,
  modeConversion: ParallelModeConversion = 'off',
  musicXml?: string,
): PrerenderedPhraseData[] => {
  const shouldApplyModeConversion = modeConversion !== 'off' && !!musicXml;
  if (semitoneSteps === 0 && !shouldApplyModeConversion && !musicXml) {
    return serializedIterator;
  }

  const keyContextsByMeasure = musicXml ? getMusicXmlKeyContextsByMeasure(musicXml) : undefined;

  return serializedIterator.map((phraseSlice) => {
    const keyContext = keyContextsByMeasure?.get(phraseSlice.currentMeasureIndex) ?? null;
    const targetKey = keyContext
      ? transposeKeyContext(keyContext, semitoneSteps, modeConversion)
      : null;

    return {
      ...phraseSlice,
      currentVoiceEntries: phraseSlice.currentVoiceEntries.map((voiceEntry) => ({
        ...voiceEntry,
        Notes: voiceEntry.Notes.map((note) => {
          if (note.isRest) {
            return note;
          }

          const sourceMidiNote = Math.round(note.pitch.halfTone + 12);
          const modeDelta = getParallelModeConversionPitchDelta(
            sourceMidiNote,
            keyContext,
            modeConversion,
          );
          const totalSemitoneSteps = modeDelta + semitoneSteps;
          if (totalSemitoneSteps === 0 && !targetKey) {
            return note;
          }

          return {
            ...note,
            pitch: transposePitchBySemitones(note.pitch, totalSemitoneSteps, targetKey?.fifths),
          };
        }),
      })),
    };
  });
};
